import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/api/client';

const BASE_CONFIG = {
  overlayOpacity: 0.72,
  stagePadding: 0,
  stageRadius: 10,
  popoverClass: 'andhub-tour-popover',
  showProgress: true,
  progressText: '{{current}} of {{total}}',
  nextBtnText: 'Next →',
  prevBtnText: '← Back',
  doneBtnText: 'Finish',
};

const STEPS = [
  {
    popover: {
      title: 'Welcome to AndHub',
      description: "Let's take a quick tour of the platform. It takes about a minute.",
    },
  },
  {
    element: '[data-tour="sidebar"]',
    popover: {
      title: 'Navigation',
      description: 'All your pages are here. The sidebar collapses to icons when you need more room.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="mark-attendance"]',
    popover: {
      title: 'Clock In',
      description: "Once you register a passkey, you'll be able to clock in directly from here to mark your daily attendance.",
      side: 'left',
      align: 'center',
    },
  },
  {
    element: '[data-tour="nav-passkeys"]',
    popover: {
      title: 'My Passkey',
      description: 'Head here to register your biometric passkey — fingerprint or Face ID. Once set up, the Clock In button on your dashboard will be unlocked.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-tour="nav-my-records"]',
    popover: {
      title: 'My Records',
      description: 'Your full attendance history. Filter by date range and preview how pending requests will affect your records.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-tour="nav-my-calendar"]',
    popover: {
      title: 'My Calendar',
      description: 'A monthly view of your attendance, approved leaves, and public holidays — all at a glance.',
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-tour="nav-my-requests"]',
    popover: {
      title: 'My Requests',
      description: "Submit leave or attendance correction requests and track whether they've been approved.",
      side: 'right',
      align: 'center',
    },
  },
  {
    element: '[data-tour="notifications"]',
    popover: {
      title: 'Notifications',
      description: 'Approvals, rejections, and announcements will show up here.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    popover: {
      title: "You're all set!",
      description: "That's everything. Start by registering your passkey — then you'll be ready to clock in.",
    },
  },
];

export function OnboardingTour(): null {
  const { user, patchUser } = useAuth();
  const { pathname } = useLocation();
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);

  const isEmployee = !!user && user.roles.includes('Employee');
  const isDesktop = window.innerWidth >= 1024;

  const markDone = useCallback(async () => {
    // Update in-memory + sessionStorage immediately so tour won't re-trigger
    patchUser({ tourCompletedAt: new Date().toISOString() });
    try {
      await api.post('/api/me/tour-done');
    } catch {
      // non-fatal
    }
  }, [patchUser]);

  const startTour = useCallback(() => {
    // Mark done immediately — once seen, never show again regardless of how user exits
    void markDone();
    driverRef.current = driver({
      ...BASE_CONFIG,
      steps: STEPS,
      onDestroyStarted: () => {
        driverRef.current?.destroy();
      },
      onPopoverRender: (popover, { state }) => {
        // Don't show Skip on the last step (it already has Finish)
        const isLastStep = state.activeIndex === STEPS.length - 1;
        if (isLastStep) return;

        const skipBtn = document.createElement('button');
        skipBtn.innerText = 'Skip tour';
        skipBtn.style.cssText = [
          'display:block',
          'width:100%',
          'background:none',
          'border:none',
          'cursor:pointer',
          'font-size:11.5px',
          'color:#bbb',
          'padding:8px 0 2px',
          'text-align:center',
        ].join(';');
        skipBtn.onmouseenter = () => { skipBtn.style.color = '#666'; };
        skipBtn.onmouseleave = () => { skipBtn.style.color = '#bbb'; };
        skipBtn.onclick = () => driverRef.current?.destroy();
        popover.footer.after(skipBtn);
      },
    });
    driverRef.current.drive();
  }, [markDone]);

  // Auto-start on first visit to /home — desktop only, once per account
  useEffect(() => {
    if (!isEmployee) return;
    if (!isDesktop) return;
    if (pathname !== '/home') return;
    if (user?.tourCompletedAt) return;
    const t = setTimeout(startTour, 800);
    return () => clearTimeout(t);
  }, [isEmployee, isDesktop, pathname, user?.tourCompletedAt, startTour]);

  // Manual trigger via custom event (e.g. from Settings page)
  useEffect(() => {
    const handler = () => { startTour(); };
    window.addEventListener('andhub:start-tour', handler);
    return () => window.removeEventListener('andhub:start-tour', handler);
  }, [startTour]);

  return null;
}
